import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Bot, Brain, User, AlertCircle } from 'lucide-react';
import { useBillParsing, type ParseMethod } from '@/hooks/useBillParsing';
import { SupplierSelectorModal } from './SupplierSelectorModal';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface BillActionModalProps {
  open: boolean;
  onClose: () => void;
  billIds: string[];
  workspaceId: string;
  enabledSuppliers?: Array<{ supplier_id: string; supplier_name: string }>;
}

export function BillActionModal({ open, onClose, billIds, workspaceId, enabledSuppliers = [] }: BillActionModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<ParseMethod>('parser_bot');
  const [showSupplierSelector, setShowSupplierSelector] = useState(false);
  const { triggerParse, isTriggering } = useBillParsing();

  const handleSubmit = async (supplierId?: string) => {
    if (selectedMethod === 'parser_bot' && !supplierId && enabledSuppliers.length > 1) {
      // Show supplier selector if multiple suppliers and none selected
      setShowSupplierSelector(true);
      return;
    }

    await triggerParse.mutateAsync({
      billIds,
      method: selectedMethod,
      supplierId,
      workspaceId
    });

    onClose();
  };

  const handleSupplierSelected = async (supplierId: string) => {
    setShowSupplierSelector(false);
    await handleSubmit(supplierId);
  };

  const billCount = billIds.length;
  const hasEnabledSuppliers = enabledSuppliers.length > 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Parse {billCount} Bill{billCount > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Choose a parsing method for the selected bill{billCount > 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>

          <RadioGroup value={selectedMethod} onValueChange={(v) => setSelectedMethod(v as ParseMethod)}>
            {/* Parser Bot Option */}
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
              <RadioGroupItem value="parser_bot" id="parser_bot" disabled={!hasEnabledSuppliers} />
              <div className="flex-1">
                <Label htmlFor="parser_bot" className="cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Parser Bot</span>
                    <Badge variant="default" className="text-xs">Recommended</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Use BillPortal API for vendor-specific parsing
                  </p>
                  {!hasEnabledSuppliers && (
                    <Alert variant="destructive" className="mt-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        No enabled suppliers. Enable suppliers in Sources/Add tab.
                      </AlertDescription>
                    </Alert>
                  )}
                  {hasEnabledSuppliers && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {enabledSuppliers.length} supplier{enabledSuppliers.length > 1 ? 's' : ''} enabled
                    </p>
                  )}
                </Label>
              </div>
            </div>

            {/* AI Parser Option */}
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
              <RadioGroupItem value="openai" id="openai" />
              <div className="flex-1">
                <Label htmlFor="openai" className="cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    <span className="font-semibold">AI Parser (OpenAI)</span>
                    <Badge variant="secondary" className="text-xs">Fallback</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Use GPT-4 for generic bill parsing (any vendor)
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Lower accuracy, may require manual review
                  </p>
                </Label>
              </div>
            </div>

            {/* Manual Parser Option */}
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-accent/50 cursor-pointer">
              <RadioGroupItem value="manual" id="manual" />
              <div className="flex-1">
                <Label htmlFor="manual" className="cursor-pointer">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-orange-500" />
                    <span className="font-semibold">Manual Parser</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Send to manual data entry queue
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Assign to operator for human review
                  </p>
                </Label>
              </div>
            </div>
          </RadioGroup>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isTriggering}>
              Cancel
            </Button>
            <Button onClick={() => handleSubmit()} disabled={isTriggering || (selectedMethod === 'parser_bot' && !hasEnabledSuppliers)}>
              {isTriggering ? 'Processing...' : `Parse ${billCount} Bill${billCount > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Selector Modal */}
      <SupplierSelectorModal
        open={showSupplierSelector}
        onClose={() => setShowSupplierSelector(false)}
        suppliers={enabledSuppliers}
        onSelectSupplier={handleSupplierSelected}
      />
    </>
  );
}
